/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import uuid from 'uuid/v4';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from '@els/els-react--modal';
import HistoryHelper from '@Services/HistoryHelper';
import NavigationHistory from '@Services/NavigationHistory';
import { compose } from 'redux';
import { withCombinedRouteParams } from '@AppRoutes/ConnectedRoute';
import StructureThumbnailContainer from '@Containers/StructureThumbnail';
import values from 'lodash/values';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import { hideGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { spinnerTypes } from '@Containers/GlobalSpinner/GlobalSpinnerConstants';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import { isAlertVisible } from '@Pages/Alerts/AlertsUtils';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { dispatchCreateAlert } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/AlertCreateDialogActions';
import { recordCountOrResultSizeFromHistoryItem } from '@Containers/PatentFamilyClustering/selectors';
import { Icon } from '@els/els-react--icon';
import { ButtonWithIcon, Button } from '@els/els-react--button';
import useDeleteFocusManager from '@Hooks/useDeleteFocusManager';
import { LEGACY_DIALOG_SIZE } from '@Containers/Dialogs/DialogsConstants';
import { historyReducerKey, typeOfHistoryConfirmationDialog, typeOfHistorySaveDialog } from '../Constants';
import { fetchSavedHistory, handleViewSaveModal, handleViewDeleteModal, onDelete, onSaveUnLoad } from '../HistoryAction';
import { capitalizeFirstLetter, todayDateFilter } from '../utils';

const FALLBACK_FOCUS_ID = 'tab-history-tabs-1'; // This is the id of the "Saved" tab in the History page, which will receive focus if the item being deleted is not found or after deletion is complete.

function buildDeleteButtonId(item) {
  return `history-saved-delete-${item?.databaseId}-${item?.searchId}`;
}

function getSortedGroupItems(groupItem) {
  const items = Array.isArray(groupItem?.items) ? groupItem.items : [];
  return [...items].sort((a, b) => (b?.creationTime ?? 0) - (a?.creationTime ?? 0));
}

function HistorySaved() {
  const { savedHistory, historyTypes, isSavedHistoryLoaded, showSaveModal, showDeleteModal, itemTobeDeleted, isDeleteInProgress } =
    useSelector((state) => state.get(historyReducerKey));
  const dispatch = useDispatch();

  const deleteButtonIds = useMemo(() => {
    const groups = Array.isArray(savedHistory) ? savedHistory : [];
    return groups.flatMap((groupItem) => getSortedGroupItems(groupItem).map(buildDeleteButtonId));
  }, [savedHistory]);

  const deletingItemButtonId = itemTobeDeleted ? buildDeleteButtonId(itemTobeDeleted) : null;

  useDeleteFocusManager({
    itemIds: deleteButtonIds,
    deletingId: deletingItemButtonId,
    isDeleting: isDeleteInProgress,
    fallbackFocusId: FALLBACK_FOCUS_ID,
  });

  useEffect(() => {
    dispatch(fetchSavedHistory());
    return () => {
      dispatch(hideGlobalSpinner());
      dispatch(onSaveUnLoad(spinnerTypes.progress));
      dispatch(closeDialog({ id: typeOfHistoryConfirmationDialog }));
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    };
  }, []);

  useEffect(() => {
    if (showSaveModal) {
      dispatch(
        updateDialog({
          id: typeOfHistorySaveDialog,
          dialogType: typeOfHistorySaveDialog,
        })
      );
    } else {
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    }
  }, [showSaveModal]);

  const isTypeSupported = (historyType) => historyTypes && values(historyTypes)?.indexOf(historyType) !== -1;

  if (!isSavedHistoryLoaded) {
    return (
      <div className="history-page__container">
        <div className="query__group" />
      </div>
    );
  }

  return (
    <div className="history-page__container">
      {showDeleteModal && (
        <Modal
          id={typeOfHistoryConfirmationDialog}
          type="secondary"
          width={LEGACY_DIALOG_SIZE}
          onClose={() => dispatch(handleViewDeleteModal(false))}>
          <header className="o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--middle">
            <Icon sprite="AlertSolidSquare" size="m" color="secondary" isVisible isDecorative />
            <h4 id="history-confirm-dialog-title" className="u-els-margin-left">
              Delete
            </h4>
          </header>
          <section className="u-els-margin-top-1o2 u-els-margin-bottom u-els-margin-left-3x">Are you sure?</section>
          <footer className="o-els-flex-layout o-els-flex-layout--right o-els-flex-layout--middle">
            <ButtonWithIcon
              data-e2e="history-delete-cancel-btn"
              type="secondary"
              sprite="Close"
              size="small"
              iconRight
              className="e2e-confirmation-modal-cancel"
              name="cancel"
              onClick={() => dispatch(handleViewDeleteModal(false))}>
              Cancel
            </ButtonWithIcon>
            <ButtonWithIcon
              data-e2e="history-delete-confirm-btn"
              type="primary"
              sprite="ChevronRight"
              size="small"
              iconRight
              className="e2e-confirmation-modal-submit u-els-margin-left"
              name="confirm"
              onClick={() => dispatch(onDelete(itemTobeDeleted))}>
              Yes
            </ButtonWithIcon>
          </footer>
        </Modal>
      )}
      {savedHistory.length > 0 ? (
        <div className="query__group">
          {savedHistory.map((groupItem, groupItemIndex) => {
            const items = getSortedGroupItems(groupItem);

            return (
              <section key={`savedGroupItem${groupItem.databaseName}`}>
                <span className="history__dbs__name">{groupItem.databaseName}</span>

                {items.map((item, index) => {
                  const safeContext = (item?.context ?? '').replace(/"/g, '');
                  const contextClass = groupItem.databaseName === 'Commercial Substances' ? 'commercialsubstance' : item.context;

                  return (
                    <article
                      key={`historySavedItem${groupItem.databaseName}${item.searchId}`}
                      className={`u-els-background-color-background query__item query__item--saved query__item--${contextClass}`}
                      title={`${item.comment}`}>
                      <div className="query__item__row">
                        <header className="query__item__header">
                          <h2 className="query__item__title u-els-font-size-h4">
                            <Icon sprite="SaveFile" isDecorative className="u-els-color-n6 u-els-margin-right-1o4" />
                            {`${recordCountOrResultSizeFromHistoryItem(item)} ${item.context ? capitalizeFirstLetter(safeContext) : ''}`}
                          </h2>
                          <time className="query__item__time">{item.userTime && todayDateFilter(item.userTime)}</time>
                        </header>

                        <section className="query__item__content">
                          <h3 className="query__item__title u-els-font-size-h4">{item.searchName}</h3>
                        </section>

                        <footer className="query__item__options">
                          <div className="rx-btn-wrap rx-btn-group--right history-btn-group">
                            <ButtonWithIcon
                              data-e2e="history-saved-edit-query"
                              name="edit-query"
                              type="link"
                              sprite="DuplicateDocument"
                              linkIconSize="xs"
                              iconRight
                              disabled={
                                item.historyType !== historyTypes.QUERY_BUILDER &&
                                !(item.quickSearchItem && item.quickSearchItem.whereClauseImportToken)
                              }
                              onClick={() => HistoryHelper.copyQuery(item)}
                              className="e2e-history-saved-edit-query">
                              Edit query
                            </ButtonWithIcon>

                            <ButtonWithIcon
                              id={buildDeleteButtonId(item)}
                              data-e2e="history-saved-delete"
                              name="delete"
                              type="link"
                              sprite="Trash"
                              linkIconSize="xs"
                              iconRight
                              onClick={() => dispatch(handleViewDeleteModal(true, groupItemIndex, index, { ...item }))}
                              className="e2e-history-saved-delete">
                              Delete
                            </ButtonWithIcon>

                            <ButtonWithIcon
                              data-e2e="history-saved-rename"
                              name="rename"
                              type="link"
                              sprite="SaveFile"
                              linkIconSize="xs"
                              iconRight
                              disabled={!isTypeSupported(item.historyType)}
                              onClick={() => dispatch(handleViewSaveModal(true, false, groupItemIndex, index, { ...item }))}
                              className="e2e-history-saved-rename">
                              Rename
                            </ButtonWithIcon>

                            {isAlertVisible(item) && (
                              <ButtonWithIcon
                                data-e2e="history-saved-open-alert"
                                name="alert"
                                type="link"
                                sprite="Alarm"
                                linkIconSize="xs"
                                iconRight
                                onClick={() => dispatchCreateAlert(item)}
                                className="e2e-history-saved-open-alert">
                                Alert
                              </ButtonWithIcon>
                            )}
                          </div>

                          <ButtonWithIcon
                            data-e2e="history-saved-open-view"
                            name="view"
                            type="tertiary"
                            sprite="ChevronRight"
                            iconRight
                            disabled={item.resultSize === 0}
                            onClick={() => NavigationHistory.viewHistory(item)}
                            className="e2e-history-saved-open-view">
                            View
                          </ButtonWithIcon>
                        </footer>
                      </div>

                      <section className="query__item__content">
                        <div className="query__item__description">
                          <div>
                            <div className="crumb-comment">
                              {item.batchSearch && (
                                <span className="rx-btn rx-btn--small rx-btn--basic_link rx-btn--right pull-right">
                                  <span className="rx-btn-text">
                                    <span className="rx-btn-text-container">Execution details</span>
                                  </span>
                                  <span className="rx-btn-icon">
                                    <i className="ss-navigateright button-icon" />
                                  </span>
                                </span>
                              )}
                              <span>{item.comment}</span>
                            </div>

                            {item.quickSearchItem && item.quickSearchItem.structure && (
                              <Button
                                data-e2e="history-saved-structure"
                                type="secondary"
                                className="u-els-padding-1o4 e2e-history-saved-structure"
                                onClick={() => {
                                  dispatch(
                                    openStructureDialog(uuid(), item.quickSearchItem.structure, {
                                      name: parseV3000SumFormula(item.quickSearchItem.structure),
                                      isLinearFormula: true,
                                    })
                                  );
                                }}>
                                <StructureThumbnailContainer
                                  enableThumbnail
                                  structure={item.quickSearchItem.structure}
                                  thumbnailType="history"
                                />
                              </Button>
                            )}

                            {item.structures && item?.structures?.length > 0 && (
                              <Button
                                data-e2e="history-saved-structure"
                                type="secondary"
                                key={`structure${uuid()}`}
                                className="u-els-padding-1o4 e2e-history-saved-structure"
                                onClick={() => {
                                  dispatch(
                                    openStructureDialog(uuid(), item.structures, {
                                      name: parseV3000SumFormula(item.structure || item.structures?.[0]?.structure),
                                      isLinearFormula: true,
                                    })
                                  );
                                }}>
                                <StructureThumbnailContainer
                                  enableThumbnail
                                  structure={item.structure || item.structures?.[0]?.structure}
                                  thumbnailType="history"
                                />
                              </Button>
                            )}
                          </div>
                        </div>
                      </section>
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="query__group">No saved searches found...</div>
      )}

      {isDeleteInProgress && <RxSpinnerComponent autoStart spinnerScale={1} spinnerId="history-confirmation-loading" />}
    </div>
  );
}

const EnhancedHistorySaved = compose(withCombinedRouteParams, withRouter)(HistorySaved);
export default EnhancedHistorySaved;
